'use client';

import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import type {FC, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {specChipLabel, UnitSpecKey, type UnitSpecView} from '../../model';
import {Typography} from '../primitives';
import {unitSpecIcon} from './unit-spec-icons';

export interface UnitSpecListProps {
  specs: UnitSpecView[];
}

const Card = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
  flexShrink: 0,
  paddingBlock: theme.spacing(3),
  paddingInline: theme.spacing(4),
  borderRadius: theme.radii.xl,
  border: `1px solid ${theme.palette.colors.border.neutral.subtle}`,
  backgroundColor: theme.palette.colors.surface.neutral.default,
  [theme.breakpoints.up('md')]: {
    width: 325,
  },
})) as FC<{children?: ReactNode}>;

const Row = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
})) as FC<{children?: ReactNode}>;

const IconBox = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 45,
  height: 45,
  borderRadius: theme.radii.lg,
  backgroundColor: theme.palette.colors.surface.neutral.pageAlternative,
  color: theme.palette.colors.icon.primary.default,
  '& svg': {fontSize: 20},
})) as FC<{children?: ReactNode}>;

const Text = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

export function UnitSpecList({specs}: UnitSpecListProps) {
  const {t} = useTranslation();
  const specLabel: Record<UnitSpecKey, string> = {
    [UnitSpecKey.DesignCategory]: t('property_type'),
    [UnitSpecKey.Bedrooms]: t('bedrooms'),
    [UnitSpecKey.Bathrooms]: t('bathrooms'),
    [UnitSpecKey.Area]: t('area'),
  };

  return (
    <Card>
      {specs.map(spec => (
        <Row key={spec.key}>
          <IconBox>{unitSpecIcon(spec.key, 'md')}</IconBox>
          <Text>
            <Typography
              size="body-2"
              weight="regular"
              family="primary"
              lineHeight="22"
              as="span"
              sx={{color: theme => theme.palette.colors.text.neutral.default}}
            >
              {specLabel[spec.key]}
            </Typography>
            <Typography size="body-2" weight="semiBold" family="primary" as="span" sx={{textTransform: 'capitalize'}}>
              {specChipLabel(t, spec)}
            </Typography>
          </Text>
        </Row>
      ))}
    </Card>
  );
}

UnitSpecList.displayName = 'UnitSpecList';
